/** Compare names the way a person counts.
 *
 *  Plain string comparison is character-by-character, so "Volunteer 10" sorts before "Volunteer 2" —
 *  '1' beats '2' before the '0' is ever looked at. That is why so many systems list 1, 10, 11, 2, 3,
 *  and it is at its worst exactly where it hurts: a manager reading numbered volunteer accounts off a
 *  screen at a table, trying to hand out 1 through 10 in order.
 *
 *  `numeric: true` makes the collator read runs of digits as numbers.
 *
 *  `sensitivity: "base"` is case- and accent-insensitive. NOT because two accounts can differ by
 *  case — a volunteer handle cannot: the API lower-cases the prefix, the DB constrains it to
 *  `^[a-z]{2,15}$` since 0261 (it was `^[a-z][a-z0-9]{0,13}[a-z]$` under 0149), and sign-in
 *  lower-cases whatever is typed, so "LaHoodTX2" and
 *  "lahoodtx2" are one account and always were. It is for DISPLAY NAMES, which keep whatever
 *  capitalisation the manager typed, so "LaHoodTX Volunteer 6" and "Lahoodtx Volunteer 11" sort
 *  as one run instead of two separate alphabets.
 *
 *  ONLY FOR HUMAN-FACING LABELS. Do NOT use it on ISO dates or timestamps: those are deliberately
 *  built so that plain string order IS chronological order, and numeric collation would read
 *  "2026-08-14" as a sequence of numbers rather than a date.
 */
const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

export function compareName(a: string | null | undefined, b: string | null | undefined): number {
  return collator.compare(a || "", b || "");
}

/** Convenience for the common `.sort(byName((x) => x.name))` shape. */
export function byName<T>(key: (x: T) => string | null | undefined) {
  return (a: T, b: T) => compareName(key(a), key(b));
}
